import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import { apiClient } from "./client";

// Browser request fields are public binding material; the verifier remains in LGW memory.
export type NativeUpdateAuthorizationRequest = publicOperatorAdmin.components["schemas"]["NativeUpdateAuthorizationRequest"];
const bindingKeys = ["environment", "target", "nonce", "state", "redirectUri", "codeChallenge"] as const;

export function parseNativeUpdateAuthorizationSearch(search: string): NativeUpdateAuthorizationRequest | null {
  const params = new URLSearchParams(search);
  if ([...params.keys()].length !== bindingKeys.length ||
      bindingKeys.some(key => params.getAll(key).length !== 1)) return null;
  const input = Object.fromEntries(bindingKeys.map(key => [key, params.get(key)!]));
  if (!["development", "staging"].includes(input["environment"]!) ||
      !["darwin-arm64", "windows-amd64", "windows-arm64"].includes(input["target"]!) ||
      ["nonce", "state", "codeChallenge"].some(key => !/^[A-Za-z0-9_-]{43}$/u.test(input[key]!))) return null;
  try {
    const uri = new URL(input["redirectUri"]!);
    if (uri.protocol !== "http:" || uri.hostname !== "127.0.0.1" || Number(uri.port) < 1024 ||
        Number(uri.port) > 65535 || !uri.port || uri.username || uri.password || uri.search || uri.hash ||
        uri.pathname !== "/local-gateway-update/callback" || uri.href !== input["redirectUri"]) return null;
  } catch { return null; }
  return input as unknown as NativeUpdateAuthorizationRequest;
}

export function nativeUpdateAuthenticationReturnTo(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, "https://console.invalid");
    if (url.origin !== "https://console.invalid" || url.pathname !== "/console/local-gateway-update" ||
        url.hash || !parseNativeUpdateAuthorizationSearch(url.search)) return null;
    return url.pathname + url.search;
  } catch { return null; }
}

export async function issueNativeUpdateAuthorization(request: NativeUpdateAuthorizationRequest, signal?: AbortSignal) {
  return apiClient<{ callbackUri: string; expiresAt: string }>("/local-gateway-update-authorizations", {
    method: "POST", body: JSON.stringify(request), signal,
  });
}

export function validateNativeUpdateCallback(request: NativeUpdateAuthorizationRequest, callbackUri: string) {
  const expected = new URL(request.redirectUri);
  const uri = new URL(callbackUri);
  if (uri.origin !== expected.origin || uri.pathname !== expected.pathname || uri.username || uri.password || uri.hash ||
      uri.searchParams.get("state") !== request.state ||
      !/^[A-Za-z0-9_-]{43}$/u.test(uri.searchParams.get("code") ?? "") ||
      [...uri.searchParams.keys()].sort().join(",") !== "code,state") {
    throw new Error("更新の受け渡し先を確認できません。Drive Gatewayからもう一度アップデートを実行してください。");
  }
  return uri.href;
}
